/**
 * Decides whether the sign-in page plays the PRIME alignment summon.
 *
 * Kept free of React and of WebGL so the policy can be unit tested without a
 * GPU or a DOM canvas.
 *
 * Note what this does *not* decide: whether the mark is shown. The mark is a
 * vector component (`PrimeDiamondMark`) that the hero renders unconditionally.
 * The summon is an enhancement on top of it, so every path that declines the
 * summon — reduced motion, no WebGL, a repeat visit — still gets the brand.
 */

export const INTRO_SESSION_KEY = 'dha_prime_summoning_played';

/**
 * TEMPORARY (2026-07-25, requested during review): play the full summon on
 * every page load instead of once per browser session, so the sequence can be
 * evaluated without clearing sessionStorage between looks.
 *
 * Flip back to `false` to restore once-per-session playback. The session
 * bookkeeping below is left intact and still tested, so this is a one-line
 * revert.
 */
export const ALWAYS_REPLAY_INTRO = true;

/** Wall-clock length of the sign-in cut, in milliseconds. */
export const INTRO_DURATION_MS = 3400;

/**
 * How long the canvas and the vector mark cross-fade at the hand-off.
 *
 * Short on purpose: the summon's last frame and the SVG are the same picture
 * to the pixel, so this is covering a swap, not a transition.
 */
export const HANDOFF_MS = 240;

/**
 * Beat sheet for the sign-in cut, in seconds, overriding the cinematic one.
 *
 * Re-timed rather than time-scaled. Uniformly compressing the cinematic cut
 * squeezes the drift-in to a blink, and the whole point of the opening beat is
 * that the frames are visibly *not* in register before they converge — so the
 * savings come out of the tail instead.
 */
export const LOGIN_TIMELINE = {
  driftStart: 0.0,
  driftEnd: 0.9,
  alignStart: 0.6,
  alignEnd: 2.45,
  lockAt: 2.45,
  flatStart: 2.6,
  flatEnd: 3.2,
  settled: 3.4,
};

export const INTRO_MODE = {
  /** Play the full summon, then hand off to the vector mark. */
  FULL: 'full',
  /** Skip the summon; the vector mark is already on the page. */
  RESTING: 'resting',
  /** No WebGL at all — same visible result as RESTING. */
  OFF: 'off',
};

/**
 * @param {object} deps
 * @param {Storage|null} deps.storage        Usually `window.sessionStorage`.
 * @param {boolean} deps.reduceMotion        `prefers-reduced-motion: reduce`.
 * @param {boolean} deps.webglAvailable
 * @param {boolean} [deps.enabled]           Kill switch for the whole feature.
 * @param {boolean} [deps.alwaysReplay]      Ignore the once-per-session record.
 * @returns {'full'|'resting'|'off'}
 */
export function resolveIntroMode({
  storage,
  reduceMotion,
  webglAvailable,
  enabled = true,
  alwaysReplay = ALWAYS_REPLAY_INTRO,
}) {
  if (!enabled) return INTRO_MODE.OFF;
  if (!webglAvailable) return INTRO_MODE.OFF;
  // Reduced motion still gets the mark — it is drawn in the DOM either way.
  if (reduceMotion) return INTRO_MODE.RESTING;
  if (!alwaysReplay && hasPlayedThisSession(storage)) return INTRO_MODE.RESTING;
  return INTRO_MODE.FULL;
}

/** Storage can throw in private/blocked contexts; never let that break sign-in. */
export function hasPlayedThisSession(storage) {
  try {
    return storage?.getItem(INTRO_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export function markIntroPlayed(storage) {
  try {
    storage?.setItem(INTRO_SESSION_KEY, '1');
  } catch {
    // Non-fatal: the intro simply plays again next load.
  }
}

/** True when the platform reports a reduced-motion preference. */
export function prefersReducedMotion(win = typeof window === 'undefined' ? null : window) {
  return Boolean(
    win && typeof win.matchMedia === 'function'
      ? win.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false,
  );
}
